import { useState, useEffect } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import SS_SelectionModal from '@/components/native-wrapper-ui/SS_SelectionModal';

import JobTaskListView from '@/components/tasks/list';

import { selectJobOverviewData, selectTasksByJobId, selectCheckinByBooking } from '@/store/selectors';
import { checkOut, updateCheckinField } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

// Stock options for hours and miles
const HOURS_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];
const MILES_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100];

export const Route = createFileRoute('/job/$jobId/$date/checkout')({
  component: () => (
    <ProtectedRoute>
      <JobCheckOutPage />
    </ProtectedRoute>
  ),
});

function JobCheckOutPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/checkout' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentLocation, setCurrentLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showMilesModal, setShowMilesModal] = useState(false);

  // Get job data from Redux
  const { booking, job } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Get tasks for this job
  const tasks = useSelector((state: RootState) => selectTasksByJobId(state, jobId));

  useEffect(() => {
    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          setLocationError('Unable to get your location');
          console.error('Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser');
    }
  }, []);

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleCheckOut = async () => {
    if (!checkinToday || checkinToday.checked_out_at) return;

    const checkOutTime = moment().format('YYYY-MM-DD HH:mm:ss');
    const hoursWorked = checkinToday.hours_total || Math.max(
      Math.round(moment().diff(checkinToday.checked_in_at, 'hours', true) * 2) / 2,
      0.5
    );

    const checkOutData = {
      ...checkinToday,
      checked_out_at: checkOutTime,
      checked_out_location: currentLocation,
      submitted_at: null,
      updated_at: checkOutTime,
      hours_total: hoursWorked,
    };

    dispatch(checkOut(checkOutData));

    // Navigate to sign-off
    navigate({ to: '/job/$jobId/$date/signoff', params: { jobId, date } });
  };

  const updateHours = (hours: number) => {
    if (!checkinToday) return;

    dispatch(updateCheckinField({
      booking_id: checkinToday.booking_id,
      booking_date: checkinToday.booking_date,
      key: 'hours_total',
      value: hours,
    }));
    setShowHoursModal(false);
  };

  const updateMiles = (miles: number) => {
    if (!checkinToday) return;

    dispatch(updateCheckinField({
      booking_id: checkinToday.booking_id,
      booking_date: checkinToday.booking_date,
      key: 'miles_total',
      value: miles,
    }));
    setShowMilesModal(false);
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  const calculateHoursWorked = () => {
    if (!checkinToday?.checked_in_at) return 0.5;

    return parseFloat(checkinToday.hours_total) || Math.max(
      Math.round(moment().diff(checkinToday.checked_in_at, 'hours', true) * 2) / 2,
      0.5
    );
  };

  const formatValue = (type: string, value: number) => {
    if (type === 'decimal') {
      return value.toFixed(1);
    }
    return value.toString();
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  const isCheckedOut = checkinToday && checkinToday.checked_out_at;

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight">
              {isCheckedOut
                ? 'You have already Checked Out Today'
                : 'What work have you completed / worked on today?'}
            </h1>

            <p className="text-base leading-relaxed mt-4">
              For each task please carry out the following…
            </p>

            <p className="text-base leading-relaxed text-black m-0">
              1. Tap the update button to the right of each task to provide details of the works you have carried out during this booking slot.
            </p>

            <p className="text-base leading-relaxed text-black mb-4">
              2. Provide photographic evidence for each task and any completed work.
            </p>
          </div>

          {/* Today's Booking Summary */}
          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              TODAY'S BOOKING
            </Label>

            <div className="space-y-2">
              {/* Hours Worked */}

                  <div className="flex items-center border border-neutral-200">
                    <div className="flex-1 flex items-center px-4 py-2">
                      <span className="text-black">Hours Worked: </span>
                      <span className="text-black font-bold ml-1">
                        {formatValue('decimal', calculateHoursWorked())} hrs
                      </span>
                    </div>

                    <Button
                      onClick={() => setShowHoursModal(true)}
                      className="h-full px-6 rounded-none border-0 bg-blue-600 hover:bg-blue-700"
                    >
                      Update
                    </Button>
                  </div>


              {/* Miles */}

                  <div className="flex items-center border border-neutral-200">
                    <div className="flex-1 flex items-center px-4 py-2">
                      <span className="text-black">Miles: </span>
                      <span className="text-black font-bold ml-1">
                        {formatValue('integer', checkinToday?.miles_total || 0)} mi
                      </span>
                    </div>

                    <Button
                      onClick={() => setShowMilesModal(true)}
                      className="h-full px-6 rounded-none border-0 bg-blue-600 hover:bg-blue-700"
                    >
                      Update
                    </Button>
                  </div>

            </div>
          </div>

          {/* Tasks */}
          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Tasks
            </Label>
            <JobTaskListView
              canUpdateTimesheet={true}
              canAddPhotos={true}
              updateJobStatus={true}
              checkin={checkinToday}
              tasks={tasks}
            />
          </div>

          {/* Current Location */}
          <div className="space-y-4 py-8 border-t border-gray-200">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Your Current Location
            </Label>

            <Card className="h-80 p-0 overflow-hidden">
              <CardContent className="p-0 h-full">

                {locationError ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-red-500">{locationError}</p>
                  </div>
                ) : currentLocation ? (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${currentLocation.latitude},${currentLocation.longitude}`}
                      width="100%"
                      height="100%"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Getting your location...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={isCheckedOut ? handleGoBack : handleCheckOut}
              className="flex-1"
              disabled={!isCheckedOut && !currentLocation}
            >
              {isCheckedOut ? 'Back to Overview' : 'Check Out of Job'}
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>

      {/* Hours Selection Modal */}
      <SS_SelectionModal
        visible={showHoursModal}
        label="Select Hours"
        closeModal={() => setShowHoursModal(false)}
      >
        <div className="grid grid-cols-4 gap-2">
          {HOURS_OPTIONS.map((hours) => (
            <Button
              key={hours}
              variant={calculateHoursWorked() === hours ? "default" : "outline"}
              onClick={() => updateHours(hours)}
              className="h-10"
            >
              {formatValue('decimal', hours)} hrs
            </Button>
          ))}
        </div>
      </SS_SelectionModal>

      {/* Miles Selection Modal */}
      <SS_SelectionModal
        visible={showMilesModal}
        label="Select Miles"
        closeModal={() => setShowMilesModal(false)}
      >
        <div className="grid grid-cols-4 gap-2">
          {MILES_OPTIONS.map((miles) => (
            <Button
              key={miles}
              variant={(checkinToday?.miles_total || 0) === miles ? "default" : "outline"}
              onClick={() => updateMiles(miles)}
              className="h-10"
            >
              {formatValue('integer', miles)} mi
            </Button>
          ))}
        </div>
      </SS_SelectionModal>
    </ScrollArea>
  );
}
